import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import type {
  EmployeeRole,
  WorkQueue,
  WorkQueueItem,
  WorkQueueLane,
  WorkQueuePriority,
} from "@care-by-ella/contracts";
import {
  fetchChatInbox,
  fetchClinicianMeetings,
  fetchTransferReviewCount,
  UnauthorizedError,
  type ChatInboxRow,
  type ClinicianMeeting,
} from "./clinician-api";
import { fetchWorkQueue } from "./work-queue-api";
import { fetchSupportInboxUnread } from "./support-inbox-api";
import { useAuth } from "@/auth/AuthContext";
import { supportViewFromPath } from "./support-view";
import { useRefreshOnFocus } from "./use-refresh-on-focus";

// TES-506 — the one place the portal counts what is waiting.
//
// The walkthrough of 2026-09-04:
// "Det är just nu tre olika inkorgar. Ingen inkorg alls, kör varje kategori som menyval med badge och lägg till en dashboard som visar det mest akuta från varje del."
//
// Both halves of that need the same numbers: the sidebar badge and the Idag
// dashboard. Fetching per surface would mean the badge and the page disagree
// for as long as their polls are out of phase — the badge saying 3 while the
// page it links to shows 4 is exactly the kind of thing that teaches a doctor
// to stop trusting the badge. So the shell fetches once, and everything reads
// from here.
//
// Every source is independently fallible: a role without access to one of them
// gets `null` for that source and a working portal, not a broken shell. Only a
// 401 propagates, and it does so by signing the session out.

const POLL_INTERVAL_MS = 30_000;

// A meeting older than this, still undocumented, is the doctor's to write up.
// Matches the Meetings route's own grace period.
const DOCUMENTATION_GRACE_MS = 60 * 60 * 1000;

export interface ClinicQueues {
  workQueue: WorkQueue | null;
  meetings: ClinicianMeeting[] | null;
  chat: ChatInboxRow[] | null;
  /** TES-1032 — prescription transfers waiting on the doctor (doctor only). */
  transferReviews: number | null;
  /** TES-1102 — unread patient messages in the Support inbox (admin only). */
  supportInbox: number | null;
  /** True until the first round of fetches has settled. */
  loading: boolean;
  reload: () => void;
}

const ClinicQueuesContext = createContext<ClinicQueues | null>(null);

/** Swallow anything that is not a 401 — see the header. */
async function soft<T>(
  load: () => Promise<T>,
  onUnauthorized: () => void,
): Promise<T | null> {
  try {
    return await load();
  } catch (e) {
    if (e instanceof UnauthorizedError) onUnauthorized();
    return null;
  }
}

export function ClinicQueuesProvider({ children }: { children: ReactNode }) {
  const { signOut, claims } = useAuth();
  const { pathname } = useLocation();
  // TES-1040 — in a support view the admin reads the doctor's or the coach's
  // queues (the API shapes them by X-Support-View-As), without the chat.
  const supportView =
    claims?.role === "admin" ? supportViewFromPath(pathname) : null;
  const role = (supportView ?? claims?.role) as EmployeeRole | undefined;
  const [workQueue, setWorkQueue] = useState<WorkQueue | null>(null);
  const [meetings, setMeetings] = useState<ClinicianMeeting[] | null>(null);
  const [chat, setChat] = useState<ChatInboxRow[] | null>(null);
  const [transferReviews, setTransferReviews] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  // signOut is stable in practice but not by contract; a ref keeps the polling
  // effect from tearing itself down and refetching on every auth re-render.
  const signOutRef = useRef(signOut);
  signOutRef.current = signOut;

  // Only clinical roles have queues at all. An admin's shell renders no badges
  // and fires no requests it would only collect 403s from.
  const clinical = role === "doctor" || role === "coach";

  // TES-1032 — the transfer count alone: one cheap request, so it can also
  // refresh on every navigation, not only on the 30-second poll.
  const loadTransferReviews = useCallback(() => {
    if (role !== "doctor") return Promise.resolve();
    return soft(fetchTransferReviewCount, () => void signOutRef.current()).then(
      (r) => setTransferReviews(r?.awaitingReview ?? null),
    );
  }, [role]);

  const reload = useCallback(() => {
    if (!clinical) {
      setLoading(false);
      return;
    }
    const bail = () => void signOutRef.current();
    void Promise.all([
      soft(fetchWorkQueue, bail).then(setWorkQueue),
      // Meetings are the doctor's; a coach has none and would get a 403.
      role === "doctor"
        ? soft(fetchClinicianMeetings, bail).then((r) =>
            setMeetings(r?.meetings ?? null),
          )
        : Promise.resolve(),
      supportView
        ? Promise.resolve()
        : soft(fetchChatInbox, bail).then((r) =>
            setChat(r?.conversations ?? null),
          ),
      loadTransferReviews(),
    ]).finally(() => setLoading(false));
  }, [clinical, role, supportView, loadTransferReviews]);

  // TES-1040 — switching between the admin's two support views changes the
  // shape of the queue; the other view's answer must not show meanwhile.
  useEffect(() => {
    setWorkQueue(null);
    setMeetings(null);
    setTransferReviews(null);
  }, [role]);

  useEffect(() => {
    reload();
    if (!clinical) return;
    const handle = setInterval(reload, POLL_INTERVAL_MS);
    return () => clearInterval(handle);
  }, [reload, clinical]);
  // TES-1383 — "Nästa möte" on Today drops a cancelled meeting as soon as
  // the doctor is back in the tab, not up to half a minute later.
  useRefreshOnFocus(reload, clinical);

  // A new transfer should not wait up to half a minute to show once the
  // doctor moves through the portal. The first render is covered by the
  // reload above, so this skips it.
  const firstPath = useRef(true);
  useEffect(() => {
    if (firstPath.current) {
      firstPath.current = false;
      return;
    }
    void loadTransferReviews();
  }, [pathname, loadTransferReviews]);

  // TES-1102 — the Support inbox badge, for anyone who acts as an admin (the
  // base role or the TES-1089 grant), in or out of a support view. Its own
  // poll: the admin has none of the clinical queues above.
  const [supportInbox, setSupportInbox] = useState<number | null>(null);
  const answersSupport =
    claims?.role === "admin" || (claims?.roles ?? []).includes("admin");
  const loadSupportInbox = useCallback(() => {
    if (!answersSupport) return Promise.resolve();
    return soft(fetchSupportInboxUnread, () => void signOutRef.current()).then(
      (r) => setSupportInbox(r?.unread ?? null),
    );
  }, [answersSupport]);
  useEffect(() => {
    void loadSupportInbox();
    if (!answersSupport) return;
    const handle = setInterval(() => void loadSupportInbox(), POLL_INTERVAL_MS);
    return () => clearInterval(handle);
  }, [loadSupportInbox, answersSupport]);

  const reloadAll = useCallback(() => {
    reload();
    void loadSupportInbox();
  }, [reload, loadSupportInbox]);

  const value = useMemo<ClinicQueues>(
    () => ({
      workQueue,
      meetings,
      chat,
      transferReviews,
      supportInbox,
      loading,
      reload: reloadAll,
    }),
    [
      workQueue,
      meetings,
      chat,
      transferReviews,
      supportInbox,
      loading,
      reloadAll,
    ],
  );

  return (
    <ClinicQueuesContext.Provider value={value}>
      {children}
    </ClinicQueuesContext.Provider>
  );
}

export function useClinicQueues(): ClinicQueues {
  const ctx = useContext(ClinicQueuesContext);
  if (!ctx) {
    throw new Error("useClinicQueues must be used inside ClinicQueuesProvider");
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// Derivations
// ---------------------------------------------------------------------------

// TES-506 — where each lane lives now that it is its own menu entry. Two lanes
// already had a surface of their own before the split and keep it: handoffs has
// the queue with its accept/decline actions, and reviews_to_approve is the
// unified review queue. The rest render through LaneQueueRoute.
//
// `onboarding` deliberately points at /health-declarations, which is the old
// /inbox route: the lane and that page project the SAME clinician.inbox() rows,
// but the page also carries the TES-458 journey filter and the assessment
// column. The word "inkorg" is what the walkthrough retired — not the filter.
export const LANE_PATH: Record<WorkQueueLane, string> = {
  // TES-1189 — coach-only, worked in the stacked /work-queue; no page of its
  // own, like adherence_risk.
  new_patients: "/new-patients",
  handoffs: "/handoffs",
  follow_ups: "/follow-ups",
  checkins: "/checkins",
  labs: "/labs",
  missed_checkins: "/missed-checkins",
  adherence_risk: "/adherence-risk",
  program_changes: "/program-changes",
  renewals: "/renewals",
  onboarding: "/health-declarations",
  side_effects: "/side-effects",
  medical_alerts: "/medical-alerts",
  medical_reviews: "/medical-reviews",
  webdoc_confirmations: "/webdoc-confirmations",
  meetings_to_document: "/meetings-to-document",
  completion_rounds: "/completion-rounds",
  doctor_question_answers: "/doctor-question-answers",
};

const PRIORITY_RANK: Record<WorkQueuePriority, number> = {
  urgent: 0,
  high: 1,
  medium: 2,
  low: 3,
};

/** Most urgent first; within a priority, the oldest deadline first. */
export function byUrgency(a: WorkQueueItem, b: WorkQueueItem): number {
  const p = PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];
  if (p !== 0) return p;
  if (a.dueAt && b.dueAt) return a.dueAt.localeCompare(b.dueAt);
  if (a.dueAt) return -1;
  if (b.dueAt) return 1;
  return 0;
}

export function laneItems(
  queue: WorkQueue | null,
  lane: WorkQueueLane,
): WorkQueueItem[] {
  return queue?.lanes.find((l) => l.lane === lane)?.items ?? [];
}

export function laneNote(
  queue: WorkQueue | null,
  lane: WorkQueueLane,
): string | null {
  return queue?.lanes.find((l) => l.lane === lane)?.note ?? null;
}

export function isToday(iso: string): boolean {
  const d = new Date(iso);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

/** Passed, undocumented — the write-up the doctor still owes. */
export function needsDocumentation(m: ClinicianMeeting): boolean {
  return (
    new Date(m.scheduledAt).getTime() < Date.now() - DOCUMENTATION_GRACE_MS &&
    m.workflowStatus !== "documented"
  );
}

/**
 * Today's meetings that are still ahead of the doctor: scheduled or running,
 * today, not yet written up. This is the number on the "Möten" badge and the
 * first block on "Idag" — the walkthrough:
 * "Prio läkare, måste jag se: upcoming meetings"
 */
export function todaysMeetings(
  meetings: ClinicianMeeting[] | null,
): ClinicianMeeting[] {
  return (meetings ?? [])
    .filter(
      (m) =>
        isToday(m.scheduledAt) &&
        m.status !== "cancelled" &&
        !needsDocumentation(m),
    )
    .sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt));
}

export function meetingsToDocument(
  meetings: ClinicianMeeting[] | null,
): ClinicianMeeting[] {
  return (meetings ?? [])
    .filter((m) => m.status !== "cancelled" && needsDocumentation(m))
    .sort((a, b) => b.scheduledAt.localeCompare(a.scheduledAt));
}

/** Patient messages nobody on the team has opened. */
export function unreadChatCount(chat: ChatInboxRow[] | null): number {
  return (chat ?? []).reduce((n, c) => n + c.unreadCount, 0);
}
